/**
 * ReciboPDF.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Plantilla de recibo profesional para A la Vuelta 360°
 * Usa fuentes nativas Helvetica/Helvetica-Bold (sin fetch externo)
 * El logo se recibe como data URL PNG convertido en BotonRecibo.js
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Svg,
  Path,
} from "@react-pdf/renderer";

// ─── Iconos SVG para footer (Helvetica no soporta emojis) ─────────────────────
const IconPhone = () => (
  <Svg width="9" height="9" viewBox="0 0 24 24">
    <Path fill="#FFFFFF" d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
  </Svg>
);

const IconMail = () => (
  <Svg width="9" height="9" viewBox="0 0 24 24">
    <Path fill="#FFFFFF" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </Svg>
);

const IconGlobe = () => (
  <Svg width="9" height="9" viewBox="0 0 24 24">
    <Path fill="#FFFFFF" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </Svg>
);

// Fuentes nativas del estándar PDF — sin fetch, siempre disponibles
const F = "Helvetica";
const FB = "Helvetica-Bold";

// ─── Paleta de colores ────────────────────────────────────────────────────────
const BRAND = {
  salmon:      "#D4846F",
  salmonLight: "#F0C4BB",
  salmonBg:    "#FDF0ED",
  dark:        "#1A1A2E",
  gray:        "#6B7280",
  grayLight:   "#E5E7EB",
  grayBg:      "#F9FAFB",
  white:       "#FFFFFF",
  black:       "#111827",
  text:        "#374151",
};

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  page: {
    fontFamily: F,
    backgroundColor: BRAND.white,
    paddingTop: 0,
    paddingBottom: 40,
    paddingHorizontal: 0,
  },

  headerBand: {
    backgroundColor: BRAND.dark,
    paddingVertical: 22,
    paddingHorizontal: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: { width: 90, height: 90 },
  logoWrapper: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: "hidden",
    backgroundColor: "transparent",
    padding: 0,
  },
  headerRight: { alignItems: "flex-end" },
  brandName: {
    fontFamily: FB,
    fontSize: 20,
    color: BRAND.white,
    letterSpacing: 2,
  },
  brandTagline: {
    fontFamily: F,
    fontSize: 9,
    color: BRAND.salmonLight,
    letterSpacing: 1,
    marginTop: 3,
  },

  titleBand: {
    backgroundColor: BRAND.salmon,
    paddingVertical: 10,
    paddingHorizontal: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  docType: {
    fontFamily: FB,
    fontSize: 14,
    color: BRAND.white,
    letterSpacing: 3,
  },
  folioBox: { alignItems: "flex-end" },
  folioLabel: {
    fontFamily: F,
    fontSize: 8,
    color: "rgba(255,255,255,0.8)",
    letterSpacing: 1,
  },
  folioNumber: {
    fontFamily: FB,
    fontSize: 18,
    color: BRAND.white,
  },

  body: { paddingHorizontal: 40, paddingTop: 24 },

  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    gap: 12,
  },
  infoBlock: {
    flex: 1,
    backgroundColor: BRAND.grayBg,
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: BRAND.salmon,
  },
  infoBlockDark: {
    flex: 1,
    backgroundColor: BRAND.grayBg,
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: BRAND.dark,
  },
  infoTitle: {
    fontFamily: FB,
    fontSize: 7,
    color: BRAND.salmon,
    letterSpacing: 1.5,
    marginBottom: 5,
  },
  infoTitleDark: {
    fontFamily: FB,
    fontSize: 7,
    color: BRAND.dark,
    letterSpacing: 1.5,
    marginBottom: 5,
  },
  infoValue: {
    fontFamily: FB,
    fontSize: 11,
    color: BRAND.black,
    marginBottom: 2,
  },
  infoSub: {
    fontFamily: F,
    fontSize: 9,
    color: BRAND.gray,
    marginBottom: 1,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: BRAND.dark,
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tableHeaderCell: {
    fontFamily: FB,
    fontSize: 8,
    color: BRAND.white,
    letterSpacing: 1,
  },
  colDesc:   { flex: 4 },
  colCant:   { flex: 2, textAlign: "center" },
  colPrecio: { flex: 2, textAlign: "right" },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.grayLight,
    alignItems: "center",
  },
  tableRowAlt: { backgroundColor: BRAND.grayBg },
  cellText: { fontFamily: F,  fontSize: 10, color: BRAND.text },
  cellBold: { fontFamily: FB, fontSize: 10, color: BRAND.black },
  cellNeg:  { fontFamily: F,  fontSize: 10, color: "#EF4444" },
  cellRight:  { textAlign: "right" },
  cellCenter: { textAlign: "center" },

  totalsWrap: { marginTop: 16, alignItems: "flex-end" },
  totalsBox:  { width: 240 },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.grayLight,
  },
  totalLabel: { fontFamily: F,  fontSize: 10, color: BRAND.gray },
  totalValue: { fontFamily: FB, fontSize: 10, color: BRAND.text },
  totalNeg:   { fontFamily: FB, fontSize: 10, color: "#EF4444" },
  totalFinalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: BRAND.dark,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 6,
    marginTop: 8,
  },
  totalFinalLabel: { fontFamily: FB, fontSize: 12, color: BRAND.white, letterSpacing: 1 },
  totalFinalValue: { fontFamily: FB, fontSize: 16, color: BRAND.salmonLight },

  estadoBadge: {
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 3,
    alignSelf: "flex-start",
    marginTop: 4,
  },
  estadoText: { fontFamily: FB, fontSize: 8, letterSpacing: 1 },

  notasBox: {
    marginTop: 20,
    backgroundColor: BRAND.salmonBg,
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: BRAND.salmon,
  },
  notasTitle: { fontFamily: FB, fontSize: 8, color: BRAND.salmon, letterSpacing: 1, marginBottom: 4 },
  notasText:  { fontFamily: F,  fontSize: 9, color: BRAND.text, lineHeight: 1.5 },

  divider: { height: 1, backgroundColor: BRAND.grayLight, marginVertical: 16 },

  footerBand: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: BRAND.salmon,
    paddingVertical: 12,
    paddingHorizontal: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: { fontFamily: F,  fontSize: 8, color: BRAND.white },
  footerBold: { fontFamily: FB, fontSize: 9, color: BRAND.white },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) => {
  const n = parseFloat(val) || 0;
  return `$${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr.includes("T") ? dateStr : dateStr + "T12:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
};

const folioStr = (id) => String(300 + (parseInt(id) || 0)).padStart(4, "0");

const estadoColor = (estado) => {
  const map = {
    Presupuesto:     { bg: "#F3F4F6", fg: "#6B7280" },
    "Por Confirmar": { bg: "#FEF3C7", fg: "#92400E" },
    Pendiente:       { bg: "#FEF3C7", fg: "#92400E" },
    Confirmado:      { bg: "#DBEAFE", fg: "#1D4ED8" },
    "En curso":    { bg: "#EDE9FE", fg: "#6D28D9" },
    Completado:      { bg: "#D1FAE5", fg: "#065F46" },
    Cancelado:       { bg: "#FEE2E2", fg: "#991B1B" },
  };
  return map[estado] ?? { bg: "#F3F4F6", fg: "#6B7280" };
};

// ─── Componente principal ─────────────────────────────────────────────────────
// logoUrl: data URL PNG generado por BotonRecibo (canvas conversion de webp)
const ReciboPDF = ({ evento, logoUrl }) => {
  if (!evento) return null;

  const costo      = parseFloat(evento.costo_servicio || evento.monto_paquete || 0);
  const anticipo   = parseFloat(evento.anticipo || evento.adelanto || 0);
  const restante   = parseFloat(evento.pago_restante || evento.restante || (costo - anticipo));
  const ec         = estadoColor(evento.estado);

  // ── Desglose: Solo servicios adicionales ────────────────────────────────
  // No incluir el paquete + tipo_servicio como primer renglón
  const itemsPrincipales = [];
  
  // ── OBTENER SERVICIOS ADICIONALES ────────────────────────────────────
  // Buscar en múltiples ubicaciones posibles (por compatibilidad)
  let serviciosAdicionales = [];
  
  // Opción 1: evento.servicios_adicionales (nuevo formato)
  if (Array.isArray(evento.servicios_adicionales) && evento.servicios_adicionales.length > 0) {
    serviciosAdicionales = evento.servicios_adicionales;
    console.log("[ReciboPDF] Servicios encontrados en evento.servicios_adicionales:", serviciosAdicionales);
  }
  // Opción 2: evento.servicios (formato alternativo del API)
  else if (Array.isArray(evento.servicios) && evento.servicios.length > 0) {
    serviciosAdicionales = evento.servicios;
    console.log("[ReciboPDF] Servicios encontrados en evento.servicios:", serviciosAdicionales);
  }
  
  // DEBUG: mostrar qué campos tiene el evento
  if (serviciosAdicionales.length === 0) {
    console.warn("[ReciboPDF] ⚠️ NO se encontraron servicios adicionales", {
      tiene_servicios_adicionales: evento.servicios_adicionales !== undefined,
      tiene_servicios: evento.servicios !== undefined,
      evento_keys: Object.keys(evento),
    });
  }
  
  // Agregar servicios adicionales como renglones separados
  if (serviciosAdicionales.length > 0) {
    serviciosAdicionales.forEach((svc) => {
      const precio = parseFloat(svc.total || svc.precio_unitario || svc.precio || 0);
      const cantidad = svc.cantidad || 1;
      
      itemsPrincipales.push({
        nombre: svc.nombre || `Servicio ${svc.servicio_id || svc.id}`,
        descripcion: svc.descripcion || "",
        cantidad: cantidad,
        precio: precio,
      });
    });
    console.log("[ReciboPDF] Items principales construidos:", itemsPrincipales);
  }

  const _ahora     = new Date();
  const generado   = _ahora.toLocaleDateString("es-MX", {
    day: "2-digit", month: "long", year: "numeric",
  });
  const horaGenerado = _ahora.toLocaleTimeString("es-MX", {
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

  return (
    <Document
      title={`Recibo-${folioStr(evento.id)}-${evento.cliente_nombre ?? "Cliente"}`}
      author="A la Vuelta 360°"
      subject="Recibo de servicio"
      creator="Agenda A la Vuelta 360°"
    >
      <Page size="A4" style={S.page}>

        {/* Cabecera oscura */}
        <View style={S.headerBand}>
          {logoUrl ? (
            <View style={S.logoWrapper}>
              <Image
                src={logoUrl}
                style={[S.logo, { objectFit: "contain", objectPositionX: "50%", objectPositionY: "50%" }]}
              />
            </View>
          ) : (
            <View style={[S.logoWrapper, { justifyContent: "center", alignItems: "center" }]}>
              <Text style={{ fontFamily: FB, fontSize: 22, color: BRAND.white }}>360</Text>
            </View>
          )}
          <View style={S.headerRight}>
            <Text style={S.brandName}>A LA VUELTA 360°</Text>
            <Text style={S.brandTagline}>TODO PARA TUS EVENTOS</Text>
          </View>
        </View>

        {/* Banda salmon */}
        <View style={S.titleBand}>
          <Text style={S.docType}>RECIBO DE SERVICIO</Text>
          <View style={S.folioBox}>
            <Text style={S.folioLabel}>N° FOLIO</Text>
            <Text style={S.folioNumber}>{folioStr(evento.id)}</Text>
          </View>
        </View>

        {/* Cuerpo */}
        <View style={S.body}>

          {/* Bloques de info */}
          <View style={S.infoRow}>
            <View style={S.infoBlock}>
              <Text style={S.infoTitle}>INFORMACIÓN DEL CLIENTE</Text>
              <Text style={S.infoValue}>{evento.cliente_nombre || "Sin nombre"}</Text>
              {evento.cliente_telefono ? <Text style={S.infoSub}>Tel: {evento.cliente_telefono}</Text> : null}
              {evento.ubicacion      ? <Text style={S.infoSub}>{evento.ubicacion}</Text> : null}
              {evento.direccion      ? <Text style={S.infoSub}>{evento.direccion}</Text> : null}
            </View>

            <View style={S.infoBlockDark}>
              <Text style={S.infoTitleDark}>DETALLES DEL EVENTO</Text>
              <Text style={S.infoValue}>{evento.titulo}</Text>
              <Text style={S.infoSub}>{fmtDate(evento.fecha_evento || evento.fecha_inicio)}</Text>
              {evento.hora_inicio ? <Text style={S.infoSub}>Hora: {evento.hora_inicio}</Text> : null}
              {(evento.tipo_servicio_nombre || evento.paquete_nombre) ? (
                <Text style={S.infoSub}>{evento.tipo_servicio_nombre || evento.paquete_nombre}</Text>
              ) : null}
              <View style={[S.estadoBadge, { backgroundColor: ec.bg }]}>
                <Text style={[S.estadoText, { color: ec.fg }]}>
                  {(evento.estado || "Sin estado").toUpperCase()}
                </Text>
              </View>
            </View>

            <View style={[S.infoBlockDark, { flex: 0.85 }]}>
              <Text style={S.infoTitleDark}>EMISIÓN</Text>
              <Text style={S.infoSub}>Generado:</Text>
              <Text style={[S.infoValue, { fontSize: 9, marginTop: 6 }]}>{generado}</Text>
              <Text style={[S.infoSub, { marginTop: 4 }]}>{horaGenerado}</Text>
            </View>
          </View>

          {/* Tabla */}
          <View style={S.tableHeader}>
            <Text style={[S.tableHeaderCell, S.colDesc]}>Paquete / Servicio</Text>
            <Text style={[S.tableHeaderCell, S.colCant]}>Cantidad</Text>
            <Text style={[S.tableHeaderCell, S.colPrecio]}>Precio</Text>
          </View>

          {itemsPrincipales.map((item, i) => (
            <View key={`p${i}`} style={S.tableRow}>
              <View style={S.colDesc}>
                <Text style={S.cellBold}>{item.nombre}</Text>
                {item.descripcion ? (
                  <Text style={[S.cellText, { fontSize: 8, marginTop: 2, color: "#6B7280" }]}>
                    {item.descripcion}
                  </Text>
                ) : null}
              </View>
              <Text style={[S.cellText, S.colCant, S.cellCenter]}>{item.cantidad}</Text>
              <Text style={[S.cellBold, S.colPrecio, S.cellRight]}>{fmt(item.precio)}</Text>
            </View>
          ))}

          {anticipo > 0 && (
            <View style={[S.tableRow, S.tableRowAlt]}>
              <Text style={[S.cellNeg, S.colDesc]}>Anticipo recibido</Text>
              <Text style={[S.cellText, S.colCant, S.cellCenter]}>—</Text>
              <Text style={[S.cellNeg, S.colPrecio, S.cellRight]}>-{fmt(anticipo)}</Text>
            </View>
          )}

          {Array.from({
            length: Math.max(0, 4 - itemsPrincipales.length - (anticipo > 0 ? 1 : 0) - (evento.servicios_adicionales?.length || 0)),
          }).map((_, i) => (
            <View key={`e${i}`} style={S.tableRow}>
              <Text style={[S.cellText, S.colDesc]}> </Text>
              <Text style={[S.cellText, S.colCant]}> </Text>
              <Text style={[S.cellText, S.colPrecio]}> </Text>
            </View>
          ))}

          {/* Totales */}
          <View style={S.totalsWrap}>
            <View style={S.totalsBox}>
              <View style={S.totalRow}>
                <Text style={S.totalLabel}>Subtotal</Text>
                <Text style={S.totalValue}>{fmt(costo)}</Text>
              </View>
              {anticipo > 0 && (
                <View style={S.totalRow}>
                  <Text style={S.totalLabel}>Anticipo pagado</Text>
                  <Text style={S.totalNeg}>-{fmt(anticipo)}</Text>
                </View>
              )}
              <View style={S.totalFinalRow}>
                <Text style={S.totalFinalLabel}>TOTAL</Text>
                <Text style={S.totalFinalValue}>{anticipo > 0 ? fmt(restante) : fmt(costo)}</Text>
              </View>
            </View>
          </View>

          {/* Notas */}
          {(evento.notas || evento.canal) && (
            <View style={S.notasBox}>
              <Text style={S.notasTitle}>NOTAS</Text>
              {evento.notas ? <Text style={S.notasText}>{evento.notas}</Text> : null}
              {evento.canal ? <Text style={[S.notasText, { marginTop: evento.notas ? 4 : 0 }]}>Canal: {evento.canal}</Text> : null}
            </View>
          )}

          <View style={S.divider} />
        </View>

        {/* Footer */}
        <View style={S.footerBand}>
          <View>
            <Text style={S.footerBold}>A la Vuelta 360°</Text>
            <Text style={S.footerText}>Los héroes Tecamac, secc. Bosques</Text>
          </View>
          <View style={{ alignItems: "center" }}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <IconGlobe />
              <Text style={[S.footerText, { marginLeft: 4 }]}>www.alavuelta360.com.mx</Text>
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <IconPhone />
              <Text style={[S.footerText, { marginLeft: 4 }]}>56 1097 9946</Text>
            </View>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <IconMail />
              <Text style={[S.footerText, { marginLeft: 4 }]}>info.alavuelta.360@gmail.com</Text>
            </View>
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default ReciboPDF;
